import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import { useTranslation } from "react-i18next";
import { useCart } from "../../../contexts/CartContext";
import { effectiveCap } from "../../../utils/cartPersistence";
import { formatCheckoutIqd } from "../../../utils/formatCheckoutIqd";
import { showToast } from "../../component/common/toastNotification";
import { NJIM } from "../../../theme/njimTheme";


const primaryBtnSx = {
  background:
    `${NJIM.walletHero} !important`,
  color: `${NJIM.white} !important`,
  borderRadius: "999px",
  textTransform: "none",
  fontWeight: 600,
  boxShadow: "0 4px 14px rgba(22,51,63,0.35) !important",
  "&:hover": {
    background:
      `${NJIM.walletHeroHover} !important`,
  },
  "&.Mui-disabled": {
    background:
      "linear-gradient(118deg, rgba(22,51,63,0.45) 0%, rgba(29,67,86,0.45) 52%, rgba(20,42,53,0.45) 100%) !important",
    color: "rgba(255,255,255,0.55) !important",
    boxShadow: "none !important",
  },
} as const;

interface Props {
  onContinue: () => void;
  onBrowse: () => void;
  continueLoading?: boolean;
}

const CheckoutCartScreen: React.FC<Props> = ({
  onContinue,
  onBrowse,
  continueLoading,
}) => {
  const { t } = useTranslation();
  const { cart, hydrated, addOrUpdateItem, removeItem } = useCart();

  if (!hydrated) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress size={44} sx={{ color: NJIM.mint }} />
      </Box>
    );
  }

  if (cart.items.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          py: 8,
          gap: 2,
        }}
      >
        <ShoppingBagOutlinedIcon sx={{ fontSize: 48, color: NJIM.textSecondary }} />
        <Typography sx={{ color: NJIM.textSecondary, fontSize: 15 }}>
          {t("salesOrder.cartEmptyTitle", { defaultValue: "Your cart is empty" })}
        </Typography>
        <Button
          variant="contained"
          disableElevation
          onClick={onBrowse}
          sx={primaryBtnSx}
        >
          {t("salesOrder.browseProducts", { defaultValue: "Browse Products" })}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography sx={{ fontWeight: 600, fontSize: 16, color: NJIM.textPrimary, mb: 2 }}>
        {t("salesOrder.yourCart")}
      </Typography>
      {cart.items.map((line) => {
        const cap = effectiveCap(line.product);
        return (
          <Box
            key={line.product.id}
            sx={{
              p: 2,
              mb: 1.5,
              borderRadius: "12px",
              border: `1px solid ${NJIM.border}`,
              bgcolor: NJIM.white,
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 1.5,
                }}
              >
                <Typography
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontWeight: 600,
                    fontSize: 14,
                    color: NJIM.textPrimary,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {line.product.name}
                </Typography>
                <Typography
                  sx={{
                    flexShrink: 0,
                    fontWeight: 600,
                    fontSize: 14,
                    color: NJIM.textPrimary,
                    whiteSpace: "nowrap",
                    textAlign: "right",
                    direction: "ltr",
                    unicodeBidi: "isolate",
                  }}
                >
                  {formatCheckoutIqd(line.product.price * line.quantity)}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    color: NJIM.textSecondary,
                    direction: "ltr",
                    unicodeBidi: "isolate",
                  }}
                >
                  {formatCheckoutIqd(line.product.price)} {t("salesOrder.each", { defaultValue: "each" })}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    bgcolor: NJIM.muted,
                    borderRadius: "999px",
                    p: 0.25,
                    flexShrink: 0,
                  }}
                >
                  <IconButton
                    size="small"
                    disabled={line.quantity <= 1}
                    onClick={() =>
                      addOrUpdateItem(line.product, line.quantity - 1)
                    }
                  >
                    -
                  </IconButton>
                  <input
                    type="number"
                    min={1}
                    max={cap}
                    value={line.quantity}
                    onChange={(e) => {
                      const parsed = parseInt(e.target.value, 10);
                      if (!Number.isFinite(parsed)) return;
                      if (parsed > cap) {
                        showToast(
                          t("salesOrder.qtyExceedsStock", {
                            defaultValue: "Quantity exceeds stock",
                          }),
                          "error"
                        );
                        addOrUpdateItem(line.product, cap);
                        return;
                      }
                      addOrUpdateItem(line.product, parsed);
                    }}
                    style={{
                      width: 36,
                      textAlign: "center",
                      border: "none",
                      background: "transparent",
                      fontWeight: 600,
                      fontSize: 13,
                      direction: "ltr",
                    }}
                  />
                  <IconButton
                    size="small"
                    disabled={line.quantity >= cap}
                    onClick={() => {
                      if (line.quantity >= cap) {
                        showToast(
                          t("salesOrder.qtyExceedsStock", {
                            defaultValue: "Quantity exceeds stock",
                          }),
                          "error"
                        );
                        return;
                      }
                      addOrUpdateItem(line.product, line.quantity + 1);
                    }}
                  >
                    +
                  </IconButton>
                </Box>
              </Box>
            </Box>
            <Button
              type="button"
              size="small"
              startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
              onClick={() => removeItem(line.product.id)}
              sx={{ mt: 1, textTransform: "none", color: NJIM.danger }}
            >
              {t("common.remove")}
            </Button>
          </Box>
        );
      })}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 2,
          mb: 2,
        }}
      >
        <Typography sx={{ fontWeight: 700 }}>{t("common.total")}</Typography>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: 20,
            color: NJIM.mint,
            direction: "ltr",
            unicodeBidi: "isolate",
          }}
        >
          {formatCheckoutIqd(cart.total)}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", gap: 1.5 }}>
        <Button variant="outlined" className="njim-secondary-btn" onClick={onBrowse}>
          {t("common.back")}
        </Button>
        <Button
          variant="contained"
          disableElevation
          fullWidth
          disabled={continueLoading}
          onClick={onContinue}
          sx={primaryBtnSx}
        >
          {continueLoading ? (
            <CircularProgress size={22} sx={{ color: NJIM.white }} />
          ) : (
            t("salesOrder.continueToReview")
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default CheckoutCartScreen;
